import {
  FIRST_TOUCH_TTL_MS,
  UTM_FIELD_BY_PARAM,
} from "~/feature/attribution/constants";
import type {
  IFirstTouch,
  IFirstTouchSource,
  IFirstTouchUtm,
  ILandingVisit,
} from "~/feature/attribution/type";
import { readReferrerDomain } from "~/feature/attribution/utils/referrerDomain";
import { UtmParam } from "~/types/utm";

const readUtm = (search: string): IFirstTouchUtm => {
  const params = new URLSearchParams(search);

  return Object.values(UtmParam).reduce<IFirstTouchUtm>((utm, param) => {
    const value = params.get(param)?.trim();

    return value ? { ...utm, [UTM_FIELD_BY_PARAM[param]]: value } : utm;
  }, {});
};

const hasAnyTag = (utm: IFirstTouchUtm): boolean => Object.keys(utm).length > 0;

const readSource = ({
  search,
  referrer,
  ownHosts,
}: ILandingVisit): IFirstTouchSource | null => {
  const utm = readUtm(search);

  if (hasAnyTag(utm)) {
    return utm;
  }

  const referrerDomain = readReferrerDomain(referrer, ownHosts);

  return referrerDomain ? { referrerDomain } : null;
};

export const buildFirstTouch = (landing: ILandingVisit): IFirstTouch | null => {
  const source = readSource(landing);

  if (!source) {
    return null;
  }

  return { ...source, firstVisitAt: new Date(landing.now).toISOString() };
};

export const isFirstTouchExpired = (
  firstTouch: IFirstTouch,
  now: number
): boolean => now - Date.parse(firstTouch.firstVisitAt) >= FIRST_TOUCH_TTL_MS;

const isActiveFirstTouch = (
  firstTouch: IFirstTouch | null,
  now: number
): firstTouch is IFirstTouch =>
  firstTouch !== null && !isFirstTouchExpired(firstTouch, now);

export const resolveFirstTouch = (
  stored: IFirstTouch | null,
  landing: ILandingVisit
): IFirstTouch | null =>
  isActiveFirstTouch(stored, landing.now) ? stored : buildFirstTouch(landing);
